import {
    ActivityRecordType,
    ReferenceContactId,
    TaskTypeId,
    USER_TEXT_MAX_LENGTH,
} from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import {
    activityTypeChoicesForRecordType,
    ActivityTypeOption,
    chooseActivityTypeLabel,
    noActivityTypeKey,
} from "components/tasks/activity-type-options";
import { DeleteButton } from "components/ui/action-button";
import { DictationTextArea } from "components/ui/dictation-textarea";
import { PanelType } from "components/ui/panel/panel-type";
import { ResponsivePanel } from "components/ui/panel/responsive-panel";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import { SpMultiSelect } from "components/ui/sp-multi-select";
import { Spinner } from "components/ui/spinner";
import {
    attendeeCandidateOptions,
    attendeeCandidatesQueryOptions,
    attendeeLabel,
    attendeePlaceholder,
} from "components/visits/attendee-candidates";
import { useDictationSaveTracker } from "hooks/use-dictation-save-tracker";
import React, { useMemo, useState } from "react";
import type { TouchpointTypeTarget } from "./touchpoint-type-cell";

interface TouchpointEditorProps {
    target: TouchpointTypeTarget;
    recordType: ActivityRecordType | null;
    activityTypes: ActivityTypeOption[];
    onSaved: () => Promise<void>;
    onClose: () => void;
}

export const TouchpointEditor: React.FC<TouchpointEditorProps> = ({
    target,
    recordType,
    activityTypes,
    onSaved,
    onClose,
}) => {
    const trpcClient = trpc.useTRPC();
    const [taskTypeId, setTaskTypeId] = useState<TaskTypeId | null>(target.taskTypeId);
    const [note, setNote] = useState(target.note ?? "");
    const [involvedContactIds, setInvolvedContactIds] = useState<ReferenceContactId[]>(
        target.involvedContactIds,
    );
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const dictationSaveTracker = useDictationSaveTracker("touchpoint-note");
    const showsAttendeePicker = !!target.businessId;
    const attendeeCandidatesQuery = useQuery(
        attendeeCandidatesQueryOptions(
            trpcClient,
            showsAttendeePicker ? target.businessId : null,
        ),
    );
    const attendeeOptions = useMemo(
        () => attendeeCandidateOptions(attendeeCandidatesQuery.data),
        [attendeeCandidatesQuery.data],
    );
    const update = useMutation({
        ...trpcClient.touchpoint.update.mutationOptions(),
        onSuccess: async () => {
            await onSaved();
            onClose();
        },
        onError: error =>
            setErrorMessage(error.message || "We could not save this activity."),
    });
    const deleteTouchpoint = useMutation({
        ...trpcClient.touchpoint.delete.mutationOptions(),
        onSuccess: async () => {
            await onSaved();
            onClose();
        },
        onError: error =>
            setErrorMessage(error.message || "We could not delete this activity."),
    });

    const choices = activityTypeChoicesForRecordType(
        activityTypes,
        target.category,
        recordType,
        taskTypeId,
    );

    const chooseType = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setTaskTypeId(
            e.target.value === noActivityTypeKey ? null : (e.target.value as TaskTypeId),
        );
    };

    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        setErrorMessage(null);
        dictationSaveTracker.recordSaved(note);
        update.mutate({
            touchpointId: target.touchpointId,
            activityTypeId: taskTypeId,
            note: note.trim() ? note.trim() : null,
            involvedContactIds: showsAttendeePicker ? involvedContactIds : [],
        });
    };

    return (
        <ResponsivePanel
            isOpen
            onDismiss={onClose}
            type={PanelType.medium}
            headerText="Edit activity">
            <form onSubmit={submit} className="space-y-4">
                {choices.length > 0 ? (
                    <Select
                        label="Activity type"
                        value={taskTypeId ?? noActivityTypeKey}
                        onChange={chooseType}>
                        <option value={noActivityTypeKey}>
                            {chooseActivityTypeLabel}
                        </option>
                        {choices.map(choice => (
                            <option key={choice.id} value={choice.id}>
                                {choice.name}
                            </option>
                        ))}
                    </Select>
                ) : null}
                <DictationTextArea
                    label="Note (optional)"
                    value={note}
                    placeholder="Add a note about this activity"
                    onValueChange={setNote}
                    onDictationEnd={dictationSaveTracker.recordDictated}
                    source="touchpoint-note"
                    characterLimit={USER_TEXT_MAX_LENGTH}
                />
                {showsAttendeePicker ? (
                    <SpMultiSelect
                        label={attendeeLabel}
                        options={attendeeOptions}
                        value={involvedContactIds}
                        onChange={setInvolvedContactIds}
                        placeholder={attendeePlaceholder(attendeeOptions)}
                    />
                ) : null}
                {errorMessage ? (
                    <div className="text-red-600 text-sm">{errorMessage}</div>
                ) : null}
                <div className="flex items-center justify-end gap-2">
                    <span className="ml-auto">
                        <DeleteButton
                            backgroundColor="bg-white"
                            disabled={update.isPending || deleteTouchpoint.isPending}
                            onClick={() =>
                                deleteTouchpoint.mutate({
                                    touchpointId: target.touchpointId,
                                })
                            }
                            confirm={{
                                title: "Delete Activity",
                                message: "Are you sure you want to delete this activity?",
                            }}
                        />
                    </span>
                    {update.isPending || deleteTouchpoint.isPending ? <Spinner /> : null}
                    <SecondaryButton
                        type="button"
                        onClick={onClose}
                        disabled={update.isPending || deleteTouchpoint.isPending}>
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton
                        type="submit"
                        disabled={update.isPending || deleteTouchpoint.isPending}>
                        Save
                    </PrimaryButton>
                </div>
            </form>
        </ResponsivePanel>
    );
};
